import * as React from 'react';
import {
  ArrowUturnLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/20/solid';
import { Button, Grid, NormalColors, useTheme } from '@nextui-org/react';

import {
  GetAssetsDocument,
  GetAssetsQuery,
  StatusType,
  useSetAssetStatusMutation,
  useSetAssetTagsMutation,
} from '@src/generated/hooks';
import { AppFilterableListItem } from '@src/layouts/app/AppFilterableList';
import { ModalTagSelect } from '@src/layouts/feed/ModalTagSelect';

// Make an easier type
type Asset = GetAssetsQuery['igAssets'][0];

interface AssetCardFooterProps {
  asset: Asset;
}

/**
 * @name AssetCardFooter
 * @description tbd...
 */
export function AssetCardFooter(props: AssetCardFooterProps) {
  const { asset } = props;

  // Hooks
  const { theme } = useTheme();
  const [isTagSelectOpen, setIsTagSelectOpen] = React.useState(false);

  // GraphQL
  const [setAssetStatus] = useSetAssetStatusMutation({
    refetchQueries: [{ query: GetAssetsDocument }],
  });
  const [setAssetTags] = useSetAssetTagsMutation({
    refetchQueries: [{ query: GetAssetsDocument }],
  });

  // Setup
  const { tag } = asset;
  const selectedTagNames = tag.map((t) => t.name);

  // Handlers
  const onSetStatus = (status: StatusType) => {
    setAssetStatus({
      variables: {
        id: asset.id,
        status,
      },
    });
  };

  const onApprove = () => {
    // Popup a modal to select the tags for this story.
    setIsTagSelectOpen(true);
  };

  const onTagSelect = (tags?: AppFilterableListItem[]) => {
    if (tags) {
      setAssetTags({
        variables: {
          id: asset.id,
          tags: tags.map(({ id }) => ({ id })),
        },
      });
    }

    onSetStatus(StatusType.Approved);

    // Close the modal
    setIsTagSelectOpen(false);
  };

  // Markup
  const renderUnset = () => {
    return (
      <Grid.Container gap={1}>
        <Grid xs={6} justify="center">
          <Button
            auto
            flat
            color="success"
            css={{ w: 'stretch' }}
            icon={<CheckCircleIcon width={20} height={20} />}
            onPress={onApprove}
          >
            Approve
          </Button>
        </Grid>
        <Grid xs={6} justify="center">
          <Button
            auto
            color="error"
            css={{ w: 'stretch' }}
            onPress={() => onSetStatus(StatusType.Rejected)}
            icon={<XCircleIcon width={20} height={20} />}
          >
            Reject
          </Button>
        </Grid>
      </Grid.Container>
    );
  };

  const renderStatus = () => {
    const color = asset.status === 'APPROVED' ? 'success' : 'error';
    const text = asset.status === 'APPROVED' ? 'Approved' : 'Rejected';
    const icon =
      asset.status === 'APPROVED' ? (
        <CheckCircleIcon width={20} height={20} />
      ) : (
        <XCircleIcon width={20} height={20} />
      );

    return (
      <Grid.Container gap={1}>
        <Grid xs={6} justify="center">
          <Button auto light color={color} css={{ w: 'stretch' }} icon={icon}>
            {text}
          </Button>
        </Grid>
        <Grid xs={6} justify="center">
          <Button
            auto
            color={theme?.colors.neutral.value as NormalColors}
            css={{ w: 'stretch' }}
            onPress={() => onSetStatus(StatusType.Unset)}
            icon={<ArrowUturnLeftIcon width={20} height={20} />}
          >
            Unset
          </Button>
        </Grid>
      </Grid.Container>
    );
  };

  if (!asset) return null;

  return (
    <>
      {asset.status === 'UNSET' && renderUnset()}
      {asset.status !== 'UNSET' && renderStatus()}
      {isTagSelectOpen && (
        <ModalTagSelect
          visible={isTagSelectOpen}
          onOk={onTagSelect}
          onCancel={() => setIsTagSelectOpen(false)}
          selected={selectedTagNames}
        />
      )}
    </>
  );
}
